import { DARK_THEME_PRIMARY } from "constants/";
import {
  BestTicketBlockDetails,
  SeatGroupDetails,
  SectionDetails,
  TenantSubdomain,
  TenantType,
} from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import L, { LatLngBoundsExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useRef, useState } from "react";
import { SectionDetailsOrBestBlockDetailsArray } from "../StandardSeatSelectionFlow";
import "./VenueSeatMap.css";

export interface VenueSeatMapProps {
  tenantType: TenantType;
  tenantSubdomain: TenantSubdomain;
  eventType: string;
  venueName: string;
  theme: string;
  selectedPartySize: number;
  blocks: SectionDetailsOrBestBlockDetailsArray;
  preferredSections?: string[];
  venueMapKey?: number; // used to refresh map
  usingStaticImage?: boolean;
  seatGroups: SeatGroupDetails[];
  viewBoxWidth?: number;
  viewBoxHeight?: number;
  onSectionRemoved?: (sections: string) => void;
  onSectionAdded?: (sections: string) => void;
  onReset?: () => void;
  includePremium?: boolean;
  mapId: string;
  multipleSectionsAllowed?: boolean; // If true, multiple sections can be selected
}

const VenuSeatMap: React.FC<VenueSeatMapProps> = ({
  tenantType,
  tenantSubdomain,
  eventType,
  venueName,
  theme,
  selectedPartySize,
  blocks,
  venueMapKey,
  usingStaticImage = false,
  seatGroups,
  viewBoxWidth,
  viewBoxHeight,
  onSectionAdded,
  onSectionRemoved,
  onReset,
  preferredSections = [],
  includePremium = true,
  mapId,
  multipleSectionsAllowed = true,
}) => {
  const sectionsSet = useRef<Set<string>>();
  const mapRef = useRef<L.Map | null>(null);
  const [seatMapLoaded, setSeatMapLoaded] = useState(false);
  const isDark = useAppSelector((state) => state.theme.theme === "dark");

  const seatMapUrl = `https://branding.fanrally.com/seat_maps/${tenantType}/${tenantSubdomain}/${eventType}/${venueName}.svg`;

  function getGroups() {
    return document.querySelectorAll(
      "*[data-sections]"
    ) as NodeListOf<HTMLElement>;
  }

  function makeSectionsSet(
    blocks: BestTicketBlockDetails[] | SectionDetails[]
  ) {
    const sections = new Set(blocks.map((block) => block.section)); // [104,105,112,113,...])
    sectionsSet.current = sections;
  }

	  function hasSection(sections: string) {
	    const splittedSections = sections.split(",");
	    let hasSectionFlag = false;
	    for (const section of splittedSections) {
	      const normalizedSection = section.trim();
	      if (sectionsSet.current && sectionsSet.current.has(normalizedSection)) {
	        hasSectionFlag = true;
	        break;
	      }
	    }
	    return hasSectionFlag;
	  }

  function groupHasSection(group: HTMLElement, sections: string) {
    return group.dataset.sections === sections;
  }

  function polygonClick(groups: NodeListOf<HTMLElement>, group: HTMLElement) {
    // filter section list
    // emit filter section event
    const sections = group.dataset.sections;
    let sectionSelected = false;
    groups.forEach((g) => {
      // handles multiple sections with same section name
      if (groupHasSection(g, sections!)) {
        if (g.classList.contains("selected")) {
          g.classList.remove("selected");
          sectionSelected = true;
        } else {
          if (!multipleSectionsAllowed) {
            // If multiple sections are not allowed, remove all selected sections first
            removeSelected();
          }
          g.classList.add("selected");
        }
      }
    });

    if (sectionSelected) {
      onSectionRemoved && onSectionRemoved(sections!);
    } else {
      onSectionAdded && onSectionAdded(sections!);
    }
  }

  function greyOutSection(group: HTMLElement) {
    group.classList.remove("active");
    group.classList.add("inactive");
    fillGroupWithColor(group, "var(--section-inactive)");
  }

  function fillGroupWithColor(group: HTMLElement, color: string) {
    for (let i = 0; i < group.childNodes.length; i++) {
      // list possible svg elements
      ["rect", "path", "polygon"].forEach((elementName) => {
        const elements = group.querySelectorAll(
          elementName
        ) as NodeListOf<HTMLElement>;
        elements.forEach((element) => (element.style.fill = color));
      });
    }
  }

  function removeSelected() {
    const groups = getGroups();
    groups.forEach((g) => {
      // only one section selected at a time
      g.classList.remove("selected");
    });
  }

	  function findSeatGroupColorBySecions(sections: string) {
	    const splittedSections = sections.split(",").map((s) => s.trim()); // ["1","2","3".....]
	    // Check if section overlaps with some section in a seat group.
	    // Note: Some endpoints may return seat groups without `seatGroupSections` populated.
	    for (const seatGroup of seatGroups) {
	      if (venueName !== seatGroup.venueName) continue;
	      if (eventType !== seatGroup.eventType) continue;
	      const seatGroupSections = seatGroup.seatGroupSections ?? [];
	      for (const seatGroupSection of seatGroupSections) {
	        if (splittedSections.includes(seatGroupSection.section)) {
	          return seatGroup.color;
	        }
	      }
	    }
	    return undefined;
	  }

  function initPolygonGroups() {
    const groups = document.querySelectorAll(
      "*[data-sections]"
    ) as NodeListOf<HTMLElement>;

    groups.forEach((g) => {
      if (!hasSection(g.dataset.sections!)) {
        // make the text grey to indicate section is unavailable to select
        greyOutSection(g);
        return;
      }

      g.classList.remove("inactive");
      g.classList.add("active");

      // feature: A seat group might have a color associated, if it does,
      // then on the seat map, fill this section with the associated color
      const seatGroupColor = findSeatGroupColorBySecions(g.dataset.sections!);
      if (seatGroupColor) {
        fillGroupWithColor(g, seatGroupColor);
      } else {
        fillGroupWithColor(g, "var(--theme-color)");
      }
      g.addEventListener("click", (e) => {
        e.isTrusted && polygonClick(groups, g);
        e.stopImmediatePropagation(); // prevent event from firing twice
      });
    });
  }

  function makeSectionsSetAndInitPolygonGroups(
    blocks: BestTicketBlockDetails[] | SectionDetails[]
  ) {
    makeSectionsSet(blocks);
    requestAnimationFrame(initPolygonGroups);
  }

  function highlightPreferredSections() {
    const groups = document.querySelectorAll(
      "*[data-sections]"
    ) as NodeListOf<HTMLElement>;
    groups.forEach((group) => {
      const groupSections = group.dataset.sections?.split(",");
      if (
        groupSections?.some((section) => preferredSections.includes(section))
      ) {
        if (!group.classList.contains("selected")) {
          group.classList.toggle("selected");
        }
      }
    });
  }

  function init(svgText: string) {
    // If a map already exists, remove it first to avoid re-initialization errors
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    // Leaflet caches _leaflet_id on the container; clear it if present
    const container: any = L.DomUtil.get(mapId);
    if (container && container._leaflet_id) {
      container._leaflet_id = null;
    }

    const mymap = L.map(mapId, {
      minZoom: 2,
      maxZoom: 4,
      attributionControl: false,
      zoomControl: false,
      center: [0, 0],
      zoom: 2,
    });
    mapRef.current = mymap;
    const svgElement = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );
    svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svgElement.setAttribute(
      "viewBox",
      `0 0 ${viewBoxWidth || "500"} ${viewBoxHeight || "400"}`
    );
    svgElement.innerHTML = svgText;
    const bounds = mymap.getBounds();
    const svgElementBounds: LatLngBoundsExpression = [
      [bounds.getSouthWest().lat, bounds.getSouthWest().lng],
      [bounds.getNorthEast().lat, bounds.getNorthEast().lng],
    ];
    L.svgOverlay(svgElement, svgElementBounds, {
      interactive: true,
    }).addTo(mymap);
    const resetZoomButton = createResetZoomButton();
    resetZoomButton.setPosition("bottomright");
    resetZoomButton.addTo(mymap);
    setSeatMapLoaded(true);
  }

  function createResetZoomButton() {
    const control = new L.Control({ position: "bottomright" });
    control.onAdd = function (map) {
      const azoom = L.DomUtil.create(
        "a",
        "reset-button" // classes
      );
      azoom.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" focusable="false" width="1em" height="1em" style="-ms-transform: rotate(360deg); -webkit-transform: rotate(360deg); transform: rotate(360deg);" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><path d="M12 5c4.97 0 9 2.69 9 6c0 1.68-1.04 3.2-2.71 4.29c1.07-.87 1.71-1.97 1.71-3.16C20 9.29 16.42 7 12 7v3L8 6l4-4v3m0 14c-4.97 0-9-2.69-9-6c0-1.68 1.04-3.2 2.71-4.29C4.64 9.58 4 10.68 4 11.88C4 14.71 7.58 17 12 17v-3l4 4l-4 4v-3z" fill="#626262"/></svg>`;
      L.DomEvent.disableClickPropagation(azoom).addListener(
        azoom,
        "click",
        () => handleResetButtonClick(map),
        azoom
      );
      return azoom;
    };
    return control;
  }
  function handleResetButtonClick(map: any) {
    map.setView(map.options.center, map.options.zoom);
    removeSelected();
    onReset && onReset();
  }

  useEffect(() => {
    if (usingStaticImage) return;

    // for dev only
    // const seatMapUrl = `/devSeatMap/devSeatMap.svg`;
    fetch(seatMapUrl)
      .then((res) => res.text())
      .then((res) => {
        init(res);
        document.documentElement.style.setProperty("--theme-color", theme);
      });

    return () => {
      // Properly destroy the Leaflet map instance on unmount
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      // Ensure container is not associated with a previous map
      const container: any = L.DomUtil.get(mapId);
      if (container && container._leaflet_id) {
        container._leaflet_id = null;
      }
      document.documentElement.style.removeProperty("--theme-color");
    };
  }, []);

  // For #965 item 5
  // When “Show Premium Reservations” is unchecked, unhighlight those premium-only sections
  useEffect(() => {
    if (includePremium === false) {
      // if include premium sections checkbox is unchecked
      const newBlocks = (blocks as any[]).filter(
        (block) =>
          Number(block.perTicketCharge || block.minPlanTicketCharge || 0) === 0
      );
      makeSectionsSetAndInitPolygonGroups(newBlocks);
    } else {
      makeSectionsSetAndInitPolygonGroups(blocks);
    }
  }, [includePremium]);

  useEffect(() => {
    if (!seatMapLoaded) return;
    makeSectionsSetAndInitPolygonGroups(blocks);
  }, [blocks, seatMapLoaded]);

  useEffect(() => {
    highlightPreferredSections();
  }, [preferredSections]);

  useEffect(() => {
    removeSelected();
  }, [selectedPartySize, venueMapKey]);

  return (
    <>
      {usingStaticImage ? (
        <img
          src={`https://branding.fanrally.com/seat_maps/${tenantType}/${tenantSubdomain}/${eventType}/${venueName}.png`}
          height="95%"
        ></img>
      ) : (
        <div
          id={mapId}
          style={{
            height: "100%",
            backgroundColor: isDark ? DARK_THEME_PRIMARY : "white",
          }}
        ></div>
      )}
    </>
  );
};

export default VenuSeatMap;
